import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faStop } from "@fortawesome/free-solid-svg-icons";
import { faGlobe, faFlask } from "@fortawesome/free-solid-svg-icons";

import * as types from "types";
import type { TokenUsage } from "services/metricsApi/types/ChatRecordJson";
import type { ToolAvailability, ToolConfig } from "types";
import styles from "./MessageInput.module.scss";
import AutoExpandingTextarea from "../AutoExpandingTextarea";
import ContextGauge from "./ContextGauge";
import PlusMenu from "./PlusMenu";
import ActiveToolChips from "./ActiveToolChips";
import CompactionGauge from "./CompactionGauge/CompactionGauge";
import { ENABLE_CONTEXT_GAUGE_FOR_COMPACTION } from "config";

interface MessageInputProps {
  /**
   * Persona used to source example prompts and the context-gauge model name.
   */
  persona: types.Persona;
  userInput: string;
  setUserInput: React.Dispatch<React.SetStateAction<string>>;
  /**
   * Called when the user sends a message.
   */
  onSend: (message: types.UserMessage) => void;
  /**
   * Called when the user pastes one or more files into the input area.
   */
  onPasteFiles?: (files: File[]) => void;
  /**
   * Called when the user drops files via the PlusMenu file picker.
   */
  onFileDrop?: (files: File[]) => void;
  /**
   * Called when the user clicks the stop button while a request is in flight.
   */
  onStop?: () => void;
  /**
   * Disables the message input preventing the user from submitting messages.
   */
  disabled?: boolean;
  /**
   * Disables the file upload button in the PlusMenu.
   */
  uploadDisabled?: boolean;
  /**
   * Token usage from the most recent assistant response. Used to compute the
   * context-used gauge percentage. When absent, the gauge silently hides.
   */
  lastTokenUsage?: TokenUsage;
  /**
   * True when the most recent turn's compaction was attempted but failed or
   * was skipped. Drives the gauge's failure indicator.
   */
  compactionFailed?: boolean;
  /**
   * Which tools are available for the current persona. Determines which
   * tools can be toggled on vs shown as disabled in the PlusMenu.
   */
  toolAvailability: ToolAvailability;
  /**
   * When true, render the persona's example prompts above the textarea.
   * Intended for new-chat surfaces where the user benefits from
   * suggestions; suppressed in active chats where they would clutter the
   * input area.
   */
  showExamplePrompts?: boolean;
}

// Toggle state is global (not per-persona). Availability gates what's
// actually allowed — a tool only shows as enabled when both the persona
// supports it AND the user has toggled it on.
function buildToolConfigs(
  availability: ToolAvailability,
  toggleState: Record<string, boolean>
): ToolConfig[] {
  return [
    {
      key: "enableSearch",
      label: "Web Search",
      icon: faGlobe,
      enabled:
        availability.searchEnabled && (toggleState["enableSearch"] ?? false),
      disabled: !availability.searchEnabled,
      disabledReason: !availability.searchEnabled
        ? "Web search is not available"
        : undefined,
    },
    {
      key: "enableJupyterHub",
      label: "JupyterHub",
      icon: faFlask,
      enabled:
        availability.jupyterHubEnabled &&
        (toggleState["enableJupyterHub"] ?? false),
      disabled: !availability.jupyterHubEnabled,
      disabledReason: !availability.jupyterHubEnabled
        ? "JupyterHub is not available"
        : undefined,
    },
  ];
}

export default function MessageInput({
  persona,
  userInput,
  setUserInput,
  onSend,
  onStop,
  onPasteFiles,
  onFileDrop,
  disabled,
  uploadDisabled = false,
  lastTokenUsage,
  compactionFailed = false,
  toolAvailability,
  showExamplePrompts = false,
}: MessageInputProps) {
  // Only the user's on/off choices live in state. Availability (disabled,
  // disabledReason) is derived from props in buildToolConfigs so it stays
  // fresh when the persona changes without needing an effect.
  const [toggleState, setToggleState] = React.useState<Record<string, boolean>>(
    {
      enableSearch: localStorage.getItem("enableSearch") === "true",
      enableJupyterHub: localStorage.getItem("enableJupyterHub") === "true",
    }
  );
  const [plusMenuOpen, setPlusMenuOpen] = React.useState(false);

  const tools = buildToolConfigs(toolAvailability, toggleState);

  const handleToggleTool = (key: string) => {
    const newEnabled = !toggleState[key];
    localStorage.setItem(key, String(newEnabled));
    setToggleState({ ...toggleState, [key]: newEnabled });
  };

  let examplePrompts: undefined | string[] = undefined;
  if (showExamplePrompts === true) {
    examplePrompts = persona.examplePrompts ?? types.DEFAULT_EXAMPLE_PROMPTS;
  }

  let contextGauge: React.ReactNode = null;
  if (ENABLE_CONTEXT_GAUGE_FOR_COMPACTION) {
    if (
      lastTokenUsage?.trigger_history_tokens &&
      lastTokenUsage.compaction_trigger_tokens
    ) {
      contextGauge = (
        <CompactionGauge
          usedTokens={lastTokenUsage.trigger_history_tokens}
          compactionTriggerTokens={lastTokenUsage.compaction_trigger_tokens}
          truncatedMessages={lastTokenUsage.truncated_messages ?? 0}
          compactionFailed={compactionFailed}
        />
      );
    }
  } else if (lastTokenUsage) {
    const usedTokens = lastTokenUsage.history_tokens ?? 0;
    const maxTokens = types.getModelMaxInputTokens(persona.modelName);
    if (maxTokens) {
      contextGauge = (
        <ContextGauge
          usedTokens={usedTokens}
          maxTokens={maxTokens}
          truncatedMessages={lastTokenUsage.truncated_messages ?? 0}
        />
      );
    }
  }

  // Publishes the user's message and resets message input to "".
  const handleSend = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();

    if (onSend !== undefined) {
      const message: types.UserMessage = {
        uuid: crypto.randomUUID(),
        role: "user",
        content: userInput,
      };
      onSend(message);
    }

    setUserInput("");
  };

  // We want to have the 'enter' key send the message and 'shift' + 'enter add
  // a newline.
  const handleKeydown = (event: React.KeyboardEvent) => {
    // Did the user hit the "Enter" key without holding shift?
    const enterWithoutShift = event.key === "Enter" && !event.shiftKey;
    // If the user is holding down the keys, we only want to react to the first
    // press.
    const noRepeat = !event.repeat;

    if (enterWithoutShift && noRepeat && !disabled) {
      handleSend(event);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    if (!onPasteFiles) return;
    const files = Array.from(e.clipboardData.files).map((f) => {
      // Browsers name clipboard screenshots "image.png"; append a timestamp to make each unique.
      if (f.name !== "image.png") return f;
      const now = new Date();
      const timestamp = `${now.getFullYear()}${String(
        now.getMonth() + 1
      ).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}_${String(
        now.getHours()
      ).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}${String(
        now.getSeconds()
      ).padStart(2, "0")}`;
      return new File([f], `image_${timestamp}.png`, { type: f.type });
    });
    // If no files, let the browser handle the paste (e.g. plain text).
    if (files.length === 0) return;
    e.preventDefault();
    onPasteFiles(files);
  };

  const handleExamplePrompt = (prompt: string) => {
    if (onSend !== undefined) {
      const message: types.UserMessage = {
        uuid: crypto.randomUUID(),
        role: "user",
        content: prompt,
      };
      onSend(message);
    }
  };

  // If there are examplePrompts, add them before the messageInputBar
  const [visiblePrompts, setVisiblePrompts] = React.useState(
    examplePrompts || []
  );

  React.useEffect(() => {
    const handleResize = () => {
      const containerWidth = window.innerWidth;
      const promptWidth = 150; // Assume each prompt button is 150px wide
      const maxVisiblePrompts = Math.floor(containerWidth / promptWidth);
      if (maxVisiblePrompts < 2) {
        setVisiblePrompts([]);
      } else {
        setVisiblePrompts(examplePrompts?.slice(0, maxVisiblePrompts) || []);
      }
    };

    handleResize(); // Initial check
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [examplePrompts]);

  const examplePromptContainer = visiblePrompts.length ? (
    <div className="flex w-100 ph2 mb3 justify-center">
      <div className={styles.examplePromptContainer}>
        {visiblePrompts.map((prompt, i) => (
          <button
            key={i}
            className={styles.examplePrompt}
            onClick={() => {
              handleExamplePrompt(prompt);
            }}
          >
            {prompt}
          </button>
        ))}
      </div>
    </div>
  ) : null;

  return (
    <div>
      {examplePromptContainer}
      <div className="flex w-100 ph2 mb2 justify-center" onPaste={handlePaste}>
        <div className={styles.messageInputBar}>
          <div className={styles.inputRow}>
            <PlusMenu
              tools={tools}
              onToggleTool={handleToggleTool}
              onFileDrop={onFileDrop}
              uploadDisabled={uploadDisabled}
              isOpen={plusMenuOpen}
              setIsOpen={setPlusMenuOpen}
            />

            <AutoExpandingTextarea
              id={styles.draftMessage}
              name="draftMessage"
              className="dib hover-black w-100 bn"
              rows={1}
              aria-describedby="draftMessageDesc"
              placeholder="Send a message..."
              value={userInput}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setUserInput(e.target.value)
              }
              onKeyDown={handleKeydown}
            ></AutoExpandingTextarea>

            <div className={styles.rightControls}>
              {contextGauge}
              {disabled ? (
                onStop ? (
                  <button
                    className={styles.sendButton}
                    onClick={onStop}
                    aria-label="Stop"
                  >
                    <FontAwesomeIcon icon={faStop} beatFade />
                  </button>
                ) : (
                  <span className={styles.loader} />
                )
              ) : (
                <button
                  className={styles.sendButton}
                  onClick={handleSend}
                  disabled={disabled}
                >
                  <FontAwesomeIcon icon={faArrowRight} />
                </button>
              )}
            </div>
          </div>

          {(plusMenuOpen || tools.some((tool) => tool.enabled)) && (
            <div className={styles.chipDivider} />
          )}
          <ActiveToolChips tools={tools} isMenuOpen={plusMenuOpen} />
        </div>
      </div>
    </div>
  );
}
